<template>
  <section class="table-wrapper table-estimate">
    <ProjectListNavigation v-if="list" :list-title="list.title" />
    <v-skeleton-loader v-if="loading" type="table-tbody" />
    <div
      v-else
      :id="TABLES_IDS.PROJECT_TABLE_ID"
      class="table table-scroll div-block-3"
      @scroll="rowsScroll"
    >
      <div :style="{ ...tableWidth.style, ...tableWidth.widthStyle }" class="list-header">
        <ListHeader
          :order="order"
          :editable="isTableEditable"
          :currency="currencySymbol"
          :is-labour-divided="isLabourDivided"
          @changeName="changeColumnName"
          @addColumn="addColumn"
          @deleteColumn="deleteColumn"
          @showHourlyRate="toggleHourlyRate(true)"
          @hideHourlyRate="toggleHourlyRate(false)"
          @make-wider="(field) => updateColumnWidth('wide', field)"
          @make-narrower="(field) => updateColumnWidth('narrow', field)"
        />
      </div>
      <div
        class="list-all-rows list-all-rows-drag"
        :style="{
          ...tableWidth.widthStyle,
          top: '56px',
          position: 'absolute',
          'z-index': '1000',
          'pointer-events': 'none',
        }"
      >
        <div
          class="grid-table grid-table-scrollable grid-list"
          :style="{
            ...tableWidth.style,
            height: scrollHeight,
            ...displayRows.padding,
          }"
        >
          <div class="drag-line" :style="dragStyle" />
        </div>
      </div>
      <!--
      Virtualize the data. Assumption: all rows must be of height 46px.
      That's why the text cells are also 46ps and can be expanded.
      -->
      <div class="list-all-rows list-all-rows_content" :style="tableWidth.widthStyle">
        <div
          class="grid-table grid-table-scrollable grid-list"
          :style="{
            ...tableWidth.style,
            height: scrollHeight,
            ...displayRows.padding,
          }"
        >
          <template v-for="(row, idx) in displayRows.rows">
            <!-- If row has a group_level >= 1 – it is a Group -->
            <ListGroup
              v-if="row.group_level"
              :key="idx"
              :order="order"
              :cell-amount="tableWidth.amount"
              :editable="isTableEditable"
              :total="row.totalPrice"
              :row="row"
              :currency="currency"
              row-type="group"
              :group-closed="hiddenGroups.includes(row.id)"
              :group-has-rows="!!nestedRowsStructure[row.id].children.length"
              @add="(rowOrder) => newRow(rowOrder)"
              @addGroup="(rowOrder, level) => newRow(rowOrder, level)"
              @convertToGroup="(id, level) => convertToGroup(id, level)"
              @delete="(id) => deleteRow(id)"
              @toggle-group="toggleGroup(row.id)"
              @start-drag="handleDragStart(row.id, idx)"
              @drag="handleDrag(idx)"
              @drop="handleDrop"
            />

            <!-- Simple row -->
            <ListRow
              v-else
              :key="idx"
              :row-id="row.id"
              :order="order"
              :editable="isTableEditable"
              :row="row"
              :currency="currency"
              :style="row.transform"
              :list-title="list.title"
              :in-group="childRows.includes(row.id)"
              :show="!hiddenRows.includes(row.id)"
              row-type="row"
              @add="(rowOrder) => newRow(rowOrder)"
              @addGroup="(rowOrder, level) => newRow(rowOrder, level)"
              @convertToGroup="(id, level) => convertToGroup(id, level)"
              @delete="(id) => deleteRow(id)"
              @update="updateTable"
              @change-focused-cell="(params) => changeFocusedCell(params, row.id)"
              @start-drag="handleDragStart(row.id, idx)"
              @drag="handleDrag(idx)"
              @drop="handleDrop"
            />
          </template>
        </div>
      </div>

      <div class="table-grow" />

      <div :style="{ ...tableWidth.style, ...tableWidth.widthStyle }" class="list-taxes">
        <ListTax
          v-for="row in additionalCosts"
          :key="row.id"
          :order="order"
          :row="row"
          :editable="isTableEditable"
          row-type="tax"
          @add="(id) => newTax(id)"
          @delete="(id) => deleteTax(id)"
        />
      </div>

      <div class="list-total" :style="{ ...tableWidth.style, ...tableWidth.widthStyle }">
        <TotalRow
          type="total"
          :order="order"
          :editable="isTableEditable"
          :row="listTotal"
          :currency="currency"
          row-type="total"
        />
      </div>
    </div>
  </section>
</template>

<script>
  import { mapActions, mapGetters, mapState } from 'vuex';
  import listQuery from '@/gql/estimate/list.graphql';
  import {
    COLUMN_WIDTH_TO_GRID,
    COLUMN_WIDTHS,
    findField,
    fieldsOrder,
  } from '@/utils/fields/order-rows';
  import ListHeader from '@/components/project/list/Header';
  import ListRow from '@/components/project/list/Row';
  import ListGroup from '@/components/project/list/GroupRow';
  import { VSkeletonLoader } from 'vuetify/lib';

  import factory from '@/utils/api/factory';
  import TotalRow from '@/components/project/list/TotalRow';
  import listTotalQuery from '@/gql/estimate/list-total.graphql';
  import ListTax from '@/components/project/list/TaxRow';
  import additionalCostQuery from '@/gql/estimate/additional-cost.graphql';
  import ProjectListNavigation from '@/containers/project/ProjectListNavigation';
  import tableNavigation from '@/utils/mixins/table-navigation';
  import rangeFocusHandler from '@/utils/range-focus-handler';
  import TABLES_IDS from '@/utils/constants/tables-ids';
  import nestedRowsStructure from '@/utils/mixins/nested-rows-structure';

  const VIRTUAL_SCROLL = {
    itemHeight: 32, // The height of the single row
    display: 48, // How many rows to render
    blockSize: 8, // At what step will the virtual scroll operate
  };
  const floorToBlockSize = (num) => {
    const ready = num - (num % VIRTUAL_SCROLL.blockSize);
    return ready >= 0 ? ready : 0;
  };
  let scrollTimeout = null;

  const ORDER_OF_FIELDS_TO_EDIT = ['quantity', 'labour', 'materials', 'tools'];

  /**
   * INFO: To understand how does the table's Order works, go to the
   * {@link order_rows} and read the documentation on configuration
   */
  export default {
    name: 'ListTable',
    components: {
      ListTax,
      TotalRow,
      ListGroup,
      ListRow,
      ListHeader,
      ProjectListNavigation,
      VSkeletonLoader,
    },
    mixins: [tableNavigation, nestedRowsStructure],
    apollo: {
      $subscribe: {
        listSubscribe() {
          if (!this.silent) this.loading = true;
          return {
            query: listQuery,
            variables() {
              return { id: this.$route.params.list };
            },
            result({ data }) {
              if (data.lists?.length) {
                this.loading = false;

                const list = data.lists[0];

                this.currency = list?.project?.currency || '';

                this.order = fieldsOrder(list.fields_order || {}, list.columns_width || {});

                this.title = `${list.title || 'Untitled'} — ${
                  list?.project?.name || 'Untitled'
                } — Fixtender`;

                if (list.rows.length === 0) this.fillTheTable();

                this.list = this.groupList(list || { rows: [] });
                this.listRows = this.list.rows;
                this.updateTotalManually();

                this.createNestedRowsStructure(this.list.rows);
              } else {
                this.loading = false;
                this.$router.push({ name: 'summary' });
              }
            },
          };
        },
        listTotal: {
          query: listTotalQuery,
          variables() {
            return { id: this.$route.params.list };
          },
          result({ data }) {
            if (data.total) this.listTotal = data.total?.[0];
          },
        },
        additionalCosts: {
          query: additionalCostQuery,
          fetchPolicy: 'no-cache',
          variables() {
            return { id: this.$route.params.list };
          },
          result({ data }) {
            if (data.additional_list_cost) {
              this.additionalCosts = data.additional_list_cost;

              if (this.scrollAdditionals) {
                this.scrollAdditionals = false;
                this.$nextTick(() => {
                  const el = document.querySelector('.table.table-scroll');
                  if (el) el.scrollTo(0, el.scrollHeight);
                });
              }
            }
          },
        },
      },
    },

    data() {
      return {
        lv1: 0,
        lv2: 0,

        title: 'Fixtender',

        currency: '',

        loading: false,
        silent: false,
        order: [],

        listTotal: {},
        scrollAdditionals: false,
        additionalCosts: [],

        scrollHeight: null,
        scrollTop: 0,
        scrolledRows: -1 * VIRTUAL_SCROLL.blockSize,
        scrolledRowsPrev: 0,

        listRows: [],
        list: {},

        TABLES_IDS,
        nestedRowsStructure: {},
        hiddenGroups: [],
        hiddenRows: [],

        dragStartRow: null,
        dragStartIdx: null,
        dragLineRow: 0,
        showDragLine: false,
      };
    },

    computed: {
      ...mapState({
        // historyUpdate: (state) => state.history.updated,
        projectInfo: (state) => state.project.projectInfo,
        currencySymbols: (state) => state.global.currencySymbols,
      }),
      ...mapGetters({
        getUnreads: 'auth/getUnreads',
      }),
      dragStyle() {
        return {
          'grid-row':
            this.dragStartIdx <= this.dragLineRow ? this.dragLineRow + 2 : this.dragLineRow + 1,
          display: this.showDragLine ? 'block' : 'none',
        };
      },
      // Implements the virtual scroll by cutting the needed part of the list and displaying in
      // the grid with setting the padding-top by the amount of scrolled rows
      displayRows() {
        return {
          rows: this.listRows.slice(
            this.scrolledRows >= 0 ? this.scrolledRows : 0,
            this.scrolledRows + VIRTUAL_SCROLL.display
          ),
          padding: {
            'padding-top': this.scrolledRows * VIRTUAL_SCROLL.itemHeight + 'px',
          },
        };
      },

      // Get styles and amount of columns from the Order array
      tableWidth() {
        const widths =
          this.order.map((group) => group.fields.map((field) => field.width))?.flat() || [];
        // const pixelWidth = widths.reduce((accum, val) => accum + val.pixels, 0);

        const style = widths
          .map((space) => `minmax(${space.pixels}px, ${space.fractions})`)
          .join(' ');

        return {
          style: {
            display: 'grid',
            '-ms-grid-columns': style,
            'grid-template-columns': style,
          },
          // widthStyle: {
          //   width: `${pixelWidth}px`,
          // },
          amount: widths.length,
        };
      },

      // Only the last version can be editable
      isTableEditable() {
        // previously was checking last project version here.
        // Now project versions are deleted and this check is not need
        return true;
      },

      // Check list settings to understand if hourly rate is used (compilation of
      // working_hours * price_per_hour = labour
      isLabourDivided() {
        const settings = this.list?.settings;
        if (!settings?.length) return false;
        return settings.some((setting) => setting.type === 'with_price_per_hour');
      },
      currencySymbol() {
        return this.currencySymbols[this.currency];
      },
    },

    watch: {
      // historyUpdate: function (val) {
      //   if (val.type === 'row' || val.type === 'list' || val.type === 'additional_list_cost') {
      //     this.$apollo.queries.list.refetch();
      //   }
      // },
      '$route.params.list': function () {
        this.loading = true;
      },
    },

    methods: {
      handleDragStart(rowId, idx) {
        this.dragStartRow = rowId;
        this.dragStartIdx = idx;
        this.showDragLine = true;
      },
      handleDrag(idx) {
        this.dragLineRow = idx;
      },
      async handleDrop() {
        if (this.dragLineRow !== null) {
          await factory(`rows/${this.dragStartRow}`).postInPath('order', {
            order: this.dragLineRow,
          });
        }
        this.showDragLine = false;
        this.dragLineRow = null;
        this.dragStartIdx = null;
      },
      ...mapActions({
        openContext: 'context/open',
        openConfirm: 'confirmModal/openModal',
      }),
      async changeFocusedCell({ e, field, value, id }, rowId) {
        const rowsIds = this.displayRows.rows
          .filter((row) => !row.group_level)
          .map((row) => row.id);
        const nextPosition = await this.getNextPositionIdByLocationAndEvent({
          event: e,
          currentField: field,
          currentRowId: rowId,
          fields: ORDER_OF_FIELDS_TO_EDIT,
          rowsIds,
          value,
          tableId: TABLES_IDS.PROJECT_TABLE_ID,
        });
        // Не делаем рефокусы, если позиция не изменилась (не триггерим движение каретки при нажатии стрелочек -> <-)
        if (nextPosition && nextPosition !== id) {
          if (e.preventDefault) {
            e.preventDefault();
          }
          await rangeFocusHandler(nextPosition);
        }
      },
      countTotal(row) {
        if (row.group_level > 0) {
          return this.nestedRowsStructure[row.id];
        }
        return 0;
      },
      updateTotalManually() {
        let currentParentRow = null;

        this.listRows.forEach((row) => {
          if (row.group_level) {
            row.totalPrice = 0;
            currentParentRow = row;
          } else if (currentParentRow) {
            this.$set(
              currentParentRow,
              'totalPrice',
              currentParentRow.totalPrice + +row.total_total
            );
          }
        });
      },
      async updateTable(data) {
        let idx = this.listRows.findIndex((row) => row.id === data.id);
        this.$set(this.listRows, idx, data);

        this.updateTotalManually();
      },
      // On the table scroll event, get and set the new position, in amount of rows
      rowsScroll(e) {
        clearTimeout(scrollTimeout);
        setTimeout(() => {
          const scrolledRows =
            (e.target.scrollTop - (e.target.scrollTop % VIRTUAL_SCROLL.itemHeight)) /
              VIRTUAL_SCROLL.itemHeight || 0;

          const scrolledDiff = scrolledRows - this.scrolledRows;
          const currentBlock =
            (scrolledDiff - (scrolledDiff % VIRTUAL_SCROLL.blockSize)) / VIRTUAL_SCROLL.blockSize ||
            0;
          // If scrolled to bottom
          if (this.scrollTop < e.target.scrollTop) {
            if (currentBlock > 1)
              this.scrolledRows = floorToBlockSize(scrolledRows - VIRTUAL_SCROLL.blockSize);
          } else if (this.scrollTop > e.target.scrollTop) {
            if (
              this.scrolledRows - scrolledRows >= -1 * VIRTUAL_SCROLL.blockSize &&
              currentBlock < 2
            )
              this.scrolledRows = floorToBlockSize(scrolledRows - VIRTUAL_SCROLL.blockSize * 2 + 1);
          }
          this.scrollTop = e.target.scrollTop;
        }, 50);
      },

      // Update custom column width and locally update columns on success request
      async updateColumnWidth(value, column) {
        try {
          let width = value === 'wide' ? COLUMN_WIDTHS.wide.pixels : COLUMN_WIDTHS.narrow.pixels;
          let requestWidth = await factory('lists').postInResourcePath(
            this.list.id,
            'update_column_width',
            {
              column: column.slug,
              width,
            }
          );

          let field = findField(this.order, column.slug);
          for (let widthSlug in requestWidth.data) {
            if (field.slug === widthSlug) {
              if (Object.prototype.hasOwnProperty.call(requestWidth.data, widthSlug))
                field.width = COLUMN_WIDTH_TO_GRID(requestWidth.data[widthSlug]);
            }
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(e);
        }
      },

      // Count all occurencies of different row.group_level. These strings are used in GroupRow
      countGroup(level, listOrder) {
        if (level === 1) {
          this.lv2 = 0;
          this.lv1++;
          return `${listOrder + 1}.${this.lv1}`;
        } else {
          this.lv2++;
          return `${listOrder + 1}.${this.lv1}.${this.lv2}`;
        }
      },

      // Go through each row and assign it proper indexing and check for unread comments
      groupList(list) {
        this.lv1 = 0;
        this.lv2 = 0;
        this.scrollHeight = VIRTUAL_SCROLL.itemHeight * list.rows.length + 'px';

        return this.indexRows(list);
      },

      async fillTheTable() {
        await this.newRow(0, 1);
        await this.newRow(1, 0);
      },

      async changeColumnName({ field, name }) {
        await factory(`lists/${this.$route.params.list}/custom_field/${field}/rename`).customPost({
          name,
        });
      },

      async addColumn() {
        await factory('lists').postInResourcePath(this.$route.params.list, 'custom_field', {
          name: 'Untitled' + Math.round(Math.random() * 1000),
        });
      },

      async deleteColumn(field) {
        await factory('lists').deleteInDeepPath(this.$route.params.list, 'custom_field', field);
      },

      async toggleHourlyRate(show) {
        await factory('lists').postInResourcePath(this.$route.params.list, 'set_calculation_type', {
          calculation_type: 'with_price_per_hour',
          value: show,
        });
      },

      async newRow(order, level = 0) {
        await factory('rows').create({
          list_id: this.$route.params.list,
          order: order || order === 0 ? order + 1 : undefined,
          group_level: level,
        });
      },

      async convertToGroup(id, level) {
        await factory('rows').updateInPath(id, {
          field: 'group_level',
          value: level,
        });
      },

      async deleteRow(id) {
        this.openConfirm({
          title: this.$i18n.t('Delete this item?'),
          description: this.$i18n.t('It will be deleted forever'),
          actionText: this.$i18n.t('Delete'),
          cancelText: this.$i18n.t('Return'),
          style: 'danger',
          action: async () => {
            await factory('rows').delete(id);
          },
        });
      },

      async newTax(order = null) {
        await factory('additional_list_costs').create({
          list_id: this.$route.params.list,
          order: order || order === 0 ? order + 1 : undefined,
        });
        this.scrollAdditionals = true;
      },

      async deleteTax(id) {
        await factory('additional_list_costs').delete(id);
      },

      toggleGroup(id) {
        if (this.hiddenGroups.includes(id)) {
          this.hiddenGroups = this.hiddenGroups.filter((groupId) => groupId !== id);
          this.hiddenRows = this.hiddenRows.filter(
            (rowId) => !this.nestedRowsStructure[id].children.includes(rowId)
          );
        } else {
          this.hiddenGroups.push(id);
          this.hiddenRows.push(...this.nestedRowsStructure[id].children);
        }
      },
    },
  };
</script>
